import { getProviderJson, postProviderJson, providerError } from './providerHttp';
import { toOpenAiMessages, toOpenAiTools, emitOpenAiChoice } from './openai';

/**
 * OpenRouter adapter (BYO key, 22.08.2026).
 *
 * A normal keyed provider: the user's own `sk-or-v1-…` key goes straight to
 * openrouter.ai through the Rust HTTP layer — nothing touches Apiboo's
 * server (that is the keyless `apiboo` provider, a different adapter).
 *
 * OpenRouter speaks OpenAI's chat-completions format, so the translation is
 * the OpenAI adapter's — `toOpenAiMessages` / `toOpenAiTools` (with its
 * 1024-char description clamp, harmless here) / `emitOpenAiChoice` are
 * reused, not copied. What is deliberately NOT reused is `postChat`'s
 * `reasoning_effort` retry dance: that is an OpenAI /v1/chat/completions
 * quirk, and OpenRouter normalises reasoning per model on its side.
 *
 * Buffered only (no `sendStreaming`, no `supportsStreaming`) — the engine
 * takes the buffered path for an adapter without them.
 */

const API_URL = 'https://openrouter.ai/api/v1/chat/completions';
/** Key check: returns the key's own label/limits — no tokens generated. */
const KEY_URL = 'https://openrouter.ai/api/v1/key';

/** OpenRouter attributes traffic by these two optional headers (their docs). */
function headers(apiKey) {
  return {
    authorization: `Bearer ${apiKey}`,
    'HTTP-Referer': 'https://apiboo.com',
    'X-Title': 'Apiboo',
  };
}

export async function send({ apiKey, model, system, reference, tools, messages, onTextDelta, onToolUse, onUsage }) {
  const fullSystem = reference ? `${system}\n\n${reference}` : system;
  const body = {
    model,
    messages: toOpenAiMessages(fullSystem, messages),
    tools: toOpenAiTools(tools),
  };

  const { status, json } = await postProviderJson(API_URL, headers(apiKey), body);
  if (status >= 400 || !json) {
    throw providerError('OpenRouter', status, json?.error?.message);
  }

  return emitOpenAiChoice(json, { onTextDelta, onToolUse, onUsage });
}

export async function testKey({ apiKey }) {
  const { status, json } = await getProviderJson(KEY_URL, headers(apiKey));
  if (status >= 400 || !json) {
    throw providerError('OpenRouter', status, json?.error?.message);
  }
  return true;
}
